import { LockOutlined, Send } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Grid,
  InputAdornment,
  MenuItem,
  Typography
} from "@mui/material";
import { Form } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createConversation, getConversationModel, getConversations, getMessagingAccidents } from "../../../actions/messaging";
import snackbarUtils from "../../../utils/snackbarUtils";
import UnsavedChangesDialog from "../../commons/dialog/unsavedChangesDialog";
import FormikAutocomplete from "../../commons/formik/formikAutocomplete";
import FormikForm from "../../commons/formik/formikForm";
import FormikSelect from "../../commons/formik/formikSelect";
import FormikTextField from "../../commons/formik/formikTextField";
import { validationSchema } from "../accident/accidentDashboard/messaging/newConversationSchema";

const NewConversationForm = ({ accident = null, handleClose = null, onSuccess = null, onMessageChange = null }) => {
  const model = useSelector((state) => state.messaging.model);
  const creation = useSelector((state) => state.messaging.creation);
  const accidentsState = useSelector((state) => state.messaging.accidents);
  const selectedProvider = useSelector((state) => state.providers.selectedProvider);
  const [subtiposOptions, setSubtiposOptions] = useState([]);

  const dispatch = useDispatch();
  const accidentOptions = (accidentsState?.data || []).map((accidentOption) => ({
    ...accidentOption,
    id: accidentOption.idSiniestro,
  }));

  useEffect(() => {
    if (accident?.mlCloudCoreArtId && selectedProvider) {
      dispatch(getConversationModel(accident.mlCloudCoreArtId, selectedProvider));
    }
  }, [accident, selectedProvider, dispatch]);

  const initialValues = {
    titulo: "",
    art: accident?.mlCloudCoreArtId || "",
    nroSiniestro: accident?.idSiniestro || "",
    nroSiniestroDisplay: accident?.siniestro || "",
    tipo: "",
    subtipo: "",
    mensaje: "",
  };

  const handleChangeTipo = (tipoId, setFieldValue) => {
    setFieldValue("tipo", tipoId);
    setFieldValue("subtipo", ""); // Reset subtipo
    const selectedTipo = model?.data?.tipos?.find((t) => t.idTipoMensaje === tipoId);
    setSubtiposOptions(selectedTipo?.subtipos || []);
  };

  const handleChangeArt = (artId, setFieldValue) => {
    if (!artId) return;
    setFieldValue("art", artId);
    setFieldValue("nroSiniestro", "");
    setFieldValue("tipo", "");
    setFieldValue("subtipo", "");
    setSubtiposOptions([]);

    // Fetch Tipos for the selected ART
    dispatch(getConversationModel(artId, selectedProvider));
    // Fetch Open Accidents for the selected ART
    dispatch(getMessagingAccidents(artId, selectedProvider));
  };

  return (
    <FormikForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        const payload = {
          titulo: values.titulo,
          idTipoMensaje: values.tipo,
          idSubtipoMensaje: values.subtipo,
          idSiniestro: values.nroSiniestro,
          mensaje: values.mensaje,
          wriId: values.art,
          providerId: selectedProvider
        };

        dispatch(createConversation(payload))
          .then(() => {
            if (onSuccess) {
              snackbarUtils.success("Conversación creada correctamente");
              onSuccess();
            }
            if (accident?.idSiniestro && accident?.mlCloudCoreArtId) {
              dispatch(getConversations(accident.idSiniestro, accident.mlCloudCoreArtId));
            }
          })
          .catch(() => {
            snackbarUtils.error("Error al enviar el mensaje.");
          })
          .finally(() => {
            setSubmitting(false);
          });
      }}
      enableReinitialize
    >
      {({ values, setFieldValue, touched, errors }) => (
        <Form>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Complete los siguientes datos para enviar un nuevo mensaje.
            </Typography>
            <Alert severity="info" sx={{ mb: 3 }}>
              Al enviar este mensaje se creará una nueva conversación.
            </Alert>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormikTextField
                  name="titulo"
                  label="Título *"
                  placeholder="Ingrese un título para la conversación..."
                  variant="outlined"
                  inputProps={{ maxLength: 150 }}
                  helperText={`${values.titulo.length}/150`}
                  FormHelperTextProps={{ sx: { textAlign: "right", m: 0 } }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                {accident ? (
                  <FormikSelect
                    name="art"
                    label="ART"
                    variant="outlined"
                    disabled
                  >
                    <MenuItem value={accident.mlCloudCoreArtId}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                        {accident.art || "ART"}
                        <LockOutlined fontSize="small" color="action" />
                      </Box>
                    </MenuItem>
                  </FormikSelect>
                ) : (
                  <FormikSelect
                    name="art"
                    label="ART *"
                    variant="outlined"
                    callBack={(val) => handleChangeArt(val, setFieldValue)}
                  >
                    <MenuItem value="" disabled>Seleccionar ART</MenuItem>
                    {model?.data?.arts?.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.nombre}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                )}
              </Grid>

              <Grid item xs={12} sm={6}>
                {accident ? (
                  <FormikTextField
                    name="nroSiniestroDisplay"
                    label="N° Siniestro ART"
                    variant="outlined"
                    disabled
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <LockOutlined fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <FormikAutocomplete
                    name="nroSiniestro"
                    label="Siniestro (DNI) *"
                    options={accidentOptions}
                    optionId="id"
                    labelOption={(option) => `DNI: ${option.documento} - N° ${option.historiaClinica}`}
                    renderOption={(option) => `DNI: ${option.documento} - N° ${option.historiaClinica}`}
                    filterOptions={(option) => `${option.documento ?? ""} ${option.historiaClinica ?? ""}`}
                    helperText={touched.nroSiniestro && errors.nroSiniestro}
                    variant="outlined"
                    disabled={!values.art || accidentsState.isLoading}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <FormikSelect
                  name="tipo"
                  label="Tipo de gestión *"
                  variant="outlined"
                  callBack={(val) => handleChangeTipo(val, setFieldValue)}
                  disabled={!values.art}
                >
                  <MenuItem value="" disabled>Seleccionar tipo de gestión</MenuItem>
                  {model?.data?.tipos?.map((t) => (
                    <MenuItem key={t.idTipoMensaje} value={t.idTipoMensaje}>
                      {t.tipoMensaje}
                    </MenuItem>
                  ))}
                </FormikSelect>
              </Grid>

              <Grid item xs={12}>
                <FormikSelect
                  name="subtipo"
                  label="Consulta *"
                  variant="outlined"
                  disabled={!values.tipo}
                >
                  <MenuItem value="" disabled>Seleccionar consulta</MenuItem>
                  {subtiposOptions.map((s) => (
                    <MenuItem key={s.idSubtipoMensaje} value={s.idSubtipoMensaje}>
                      {s.subtipoMensaje}
                    </MenuItem>
                  ))}
                </FormikSelect>
              </Grid>

              <Grid item xs={12}>
                <FormikTextField
                  name="mensaje"
                  label="Mensaje *"
                  placeholder="Escriba su mensaje aquí..."
                  variant="outlined"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 5000 }}
                  helperText={`${values.mensaje.length}/5000`}
                  FormHelperTextProps={{ sx: { textAlign: "right", m: 0 } }}
                  onChange={(e) => onMessageChange && onMessageChange(e.target.value)}
                />
              </Grid>
            </Grid>

            {accident && (
              <Alert severity="info" sx={{ mt: 3 }}>
                Esta conversación quedará asociada al siniestro indicado.
              </Alert>
            )}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, pt: 2, mt: 2, borderTop: "1px solid #eee" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              endIcon={<Send />}
              disabled={creation?.isLoading}
            >
              {creation?.isLoading ? "Enviando..." : "Enviar mensaje"}
            </Button>
          </Box>
          <UnsavedChangesDialog
            shouldBlockNavigation={values.mensaje.trim().length > 0}
            title="Cambios sin guardar"
            text="¿Está seguro que desea cancelar esta operación? En caso de aceptar, se perderá el mensaje ingresado."
          />
        </Form>
      )}
    </FormikForm>
  );
};

export default NewConversationForm;
